import React, { useState, useEffect } from "react";
import { EyeIcon, EyeOffIcon, UserIcon, LockIcon, Loader2Icon, CheckCircleIcon, XCircleIcon } from "lucide-react";

interface AuthRegisterFormProps {
  onRegister: (username: string, token: string) => void;
}

export default function AuthRegisterForm({ onRegister }: AuthRegisterFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 检查用户名是否可用
  const checkUsername = async (username: string) => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    try {
      const res = await fetch("/api/check-username", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      setUsernameAvailable(data.available);
    } catch (error) {
      setUsernameAvailable(null);
    }
  };

  // 用户名输入时检查
  useEffect(() => {
    const timer = setTimeout(() => {
      if (username) {
        checkUsername(username);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  // 验证函数
  const validateForm = () => {
    // 用户名验证
    if (username.length < 3) {
      setError("用户名至少需要3个字符");
      return false;
    }
    if (username.length > 20) {
      setError("用户名不能超过20个字符");
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError("用户名只能包含字母、数字和下划线");
      return false;
    }
    if (usernameAvailable === false) {
      setError("用户名已存在，请选择其他用户名");
      return false;
    }

    // 密码验证
    if (password.length < 6) {
      setError("密码至少需要6个字符");
      return false;
    }
    if (password.length > 50) {
      setError("密码不能超过50个字符");
      return false;
    }

    // 确认密码验证
    if (password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // 表单验证
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "注册失败，请稍后重试");
      onRegister(username, data.token);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mb-4">
          <UserIcon className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">创建账户</h2>
        <p className="text-gray-600 text-sm">加入我们的社区</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 用户名输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">用户名</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              className={`w-full pl-10 pr-12 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                usernameAvailable === false 
                  ? "border-red-500 focus:ring-red-500" 
                  : usernameAvailable === true 
                  ? "border-green-500 focus:ring-green-500" 
                  : "border-gray-300"
              }`}
              type="text"
              placeholder="3-20字符，仅字母数字下划线"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              disabled={loading}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {usernameAvailable === true && (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              )}
              {usernameAvailable === false && (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
          {usernameAvailable === false && (
            <p className="text-red-600 text-sm flex items-center">
              <XCircleIcon className="h-4 w-4 mr-1" />
              用户名已存在
            </p>
          )}
          {usernameAvailable === true && (
            <p className="text-green-600 text-sm flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              用户名可用
            </p>
          )}
        </div>

        {/* 密码输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">密码</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <LockIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              type={showPassword ? "text" : "password"}
              placeholder="至少6个字符"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5 text-gray-400 hover:text-gray-300 transition-colors" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-300 transition-colors" />
              )}
            </button>
          </div>
        </div>

        {/* 确认密码输入框 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">确认密码</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <LockIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              className={`w-full pl-10 pr-12 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                confirmPassword && password !== confirmPassword 
                  ? "border-red-500 focus:ring-red-500" 
                  : confirmPassword && password === confirmPassword 
                  ? "border-green-500 focus:ring-green-500" 
                  : "border-gray-300"
              }`}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="再次输入密码"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                className="mr-2"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
              >
                {showConfirmPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                )}
              </button>
              {confirmPassword && password === confirmPassword && (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              )}
              {confirmPassword && password !== confirmPassword && (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="text-red-600 text-sm flex items-center">
              <XCircleIcon className="h-4 w-4 mr-1" />
              密码不匹配
            </p>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* 注册按钮 */}
        <button
          className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          type="submit"
          disabled={loading || usernameAvailable === false}
        >
          {loading ? (
            <>
              <Loader2Icon className="w-5 h-5 mr-2 animate-spin" />
              注册中...
            </>
          ) : (
            "创建账户"
          )}
        </button>
      </form>
    </div>
  );
} 